import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LayoutChangeEvent, StyleSheet, ViewProps, View } from 'react-native'
import { InsightTrend, InsightTrendId } from 'zens-store'
import { ChartBar, Color, Icon, Radius, Spacing, Text, titleForWeekday, Weekday } from 'zens-storybook'

import { InfoBlock } from '../info'
import { Tooltip } from '../tooltip'

import { TimeZoneConflict } from '../location'

import GraphBarSelect, { GraphBar } from './graphbarselect'

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Spacing.M,
  },
  chart: {
    minHeight: 380,
  },
  label: {
    marginTop: Spacing.S,
    alignItems: 'center',
  },
  workspaces: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: -Spacing.XXS,
  },
  count: {
    color: Color.metalGrey,
    marginLeft: Spacing.XS,
  },
  hiddenLabelMeasurement: {
    position: 'absolute',
    opacity: 0,
    left: -9999,
    top: -9999,
  },
  skeletonChart: {
    flex: 1,
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
  },
  title: {
    flex: 1,
  },
})

interface LabelWithTooltipProps {
  label: string
  truncatedLabel: string
  numberOfWorkspaces?: number
}

const LabelWithTooltip = ({ label, truncatedLabel, numberOfWorkspaces }: LabelWithTooltipProps) => {
  const [hovered, setHovered] = useState(false)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  const [fullLabelWidth, setFullLabelWidth] = useState<number | null>(null)

  const hasMeasurements = containerWidth !== null && fullLabelWidth !== null
  const overflows = hasMeasurements ? fullLabelWidth > containerWidth : false
  const shouldTruncate = overflows && label !== truncatedLabel
  const displayLabel = shouldTruncate ? truncatedLabel : label

  const handleContainerLayout = (event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width
    if (width !== containerWidth) {
      setContainerWidth(width)
    }
  }

  const handleFullLabelLayout = (event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width
    if (width !== fullLabelWidth) {
      setFullLabelWidth(width)
    }
  }

  return (
    <Tooltip
      label={label}
      visible={hovered && shouldTruncate}
      placement="bottom"
      truncateLabel={false}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <View style={styles.label} onLayout={handleContainerLayout}>
        <Text
          level={7}
          numberOfLines={shouldTruncate ? 1 : undefined}
          ellipsizeMode={shouldTruncate ? 'tail' : undefined}
        >
          {displayLabel}
        </Text>
        <Text
          level={7}
          style={styles.hiddenLabelMeasurement}
          onLayout={handleFullLabelLayout}
          accessible={false}
        >
          {label}
        </Text>
        <View style={styles.workspaces}>
          <Icon name="desk" color={Color.metalGrey} size="S" />
          <Text level={7} style={styles.count}>
            {numberOfWorkspaces}
          </Text>
        </View>
      </View>
    </Tooltip>
  )
}

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  trendId?: InsightTrendId
  insights?: InsightTrend[]
  bars?: GraphBar[]
  onChangeBars?: (bars: GraphBar[]) => void
}

const InsightsTrendGraph = (props: Props) => {
  const { companyId, buildingId, trendId, insights, bars, onChangeBars, style, ...otherProps } = props
  const { t } = useTranslation()

  const items = insights || []
  const data = [
    ...(!bars || bars.includes('average')
      ? [
          {
            values: items.map((item) => item?.value),
            renderLabel: (value: number) => `${value || 0}%`,
            backgroundColor: Color.greyBlue,
          },
        ]
      : []),
    ...(!bars || bars.includes('peak')
      ? [
          {
            values: items.map((item) => item?.peak),
            renderLabel: (value: number) => `${value || 0}%`,
            backgroundColor: Color.calmGrey,
          },
        ]
      : []),
  ]

  const horizontalAxis = {
    renderLabel: (index: number) => {
      const item = items[index]
      const rawLabel = trendId === 'weekday' ? titleForWeekday(item?.name as Weekday, t) : item?.name || ''
      const truncatedLabel = rawLabel && rawLabel.length > 10 ? rawLabel.slice(0, 10) + '...' : rawLabel
      return (
        <LabelWithTooltip
          label={rawLabel}
          truncatedLabel={truncatedLabel}
          numberOfWorkspaces={item?.numberOfWorkspaces}
        />
      )
    },
  }

  const verticalAxis = {
    max: 100,
    title: `${t('Occupancy')} (%)`,
    renderLabel: (value: number) => `${value}`,
  }

  return (
    <InfoBlock
      title={{
        text: (
          <Text numberOfLines={1} level={4} style={styles.title}>
            {trendId === 'zone'
              ? t('Occupancy by zone')
              : trendId === 'floor'
                ? t('Occupancy by floor')
                : trendId === 'workspacetype'
                  ? t('Occupancy by workspace type')
                  : t('Occupancy')}
          </Text>
        ),
        right: <GraphBarSelect value={bars} onChange={onChangeBars} />,
      }}
      style={[styles.container, style]}
      {...otherProps}
    >
      <ChartBar
        data={data}
        horizontalAxis={horizontalAxis}
        verticalAxis={verticalAxis}
        style={styles.chart}
        id="InsightsTrendGraph"
      />
      {trendId === 'weekday' && <TimeZoneConflict companyId={companyId} buildingId={buildingId} />}
    </InfoBlock>
  )
}

interface SkeletonProps extends ViewProps {
  trendId?: InsightTrendId
}
const Skeleton = (props: SkeletonProps) => {
  const { trendId, style, ...otherProps } = props
  const { t } = useTranslation()
  const title =
    trendId === 'zone'
      ? t('Occupancy by zone')
      : trendId === 'floor'
        ? t('Occupancy by floor')
        : trendId === 'workspacetype'
          ? t('Occupancy by workspace type')
          : t('Occupancy')
  return (
    <InfoBlock.Skeleton style={[styles.container, style]} {...otherProps} title={title}>
      <View style={[styles.chart, styles.skeletonChart]} />
    </InfoBlock.Skeleton>
  )
}
InsightsTrendGraph.Skeleton = Skeleton
export default InsightsTrendGraph
